<template>
  <dx-modal
    :value="show && !loading"
    :closable="!busy"
    @input="close"
    size="sm"
    id="digital-signature-modal"
  >
    <template v-slot:header>
      <div class="ds-header">
        <div class="ds-header-icon">
          <fa-icon :icon="['fal', 'signature']" />
        </div>
        <div class="ds-header-text">
          <div class="ds-header-title">Assinar digitalmente</div>
          <div class="ds-header-subtitle">
            Assinatura com certificado ICP-Brasil em nuvem, autorizada pelo app Bird ID.
          </div>
        </div>
      </div>
    </template>

    <div class="ds-providers">
      <span class="ds-providers-label">Provedor</span>
      <button type="button" class="ds-provider is-active">
        <fa-icon :icon="['fal', 'check']" />Bird ID
      </button>
      <button type="button" class="ds-provider" disabled>
        VIDaaS<span class="ds-provider-soon">em breve</span>
      </button>
    </div>

    <div class="ds-skeleton" v-if="loading">
      <div class="ds-skeleton-group" v-for="n in 3" :key="n">
        <div class="ds-skeleton-line is-label"></div>
        <div class="ds-skeleton-line is-input"></div>
      </div>
    </div>

    <template v-else-if="token.isValid">
      <div class="ds-revoke-confirm" v-if="revoking.confirm">
        <div class="ds-revoke-confirm-head">
          <div class="ds-status-icon is-error">
            <fa-icon :icon="['fal', 'ban']" />
          </div>
          <div class="ds-status-text">
            <div class="ds-status-title">Revogar autorização?</div>
            <div class="ds-status-description">
              Na próxima assinatura será preciso informar um novo código do app Bird ID.
            </div>
          </div>
        </div>
        <div class="ds-revoke-confirm-actions">
          <button
            type="button"
            class="btn btn-sm btn-error btn-icon btn-icon-left mr-1"
            :class="{ loading: revoking.loading }"
            :disabled="revoking.loading"
            @click="revoke"
          ><fa-icon :icon="['fal', 'ban']" />Revogar</button>
          <button
            type="button"
            class="btn btn-sm"
            :disabled="revoking.loading"
            @click="cancelRevoke"
          >Cancelar</button>
        </div>
      </div>

      <div class="ds-active" v-else>
        <div class="ds-status-icon is-success">
          <fa-icon :icon="['fal', 'shield-check']" />
        </div>
        <div class="ds-status-text">
          <div class="ds-status-title">Autorização ativa</div>
          <div class="ds-status-description" v-if="token.expiresAt">
            Válida até {{ expiresAtLabel }} · expira {{ expiresIn }}
          </div>
          <div class="ds-status-hint">
            Você pode assinar sem informar o código do app.
          </div>
        </div>
        <button
          type="button"
          class="btn btn-sm btn-icon btn-icon-left ds-revoke-button"
          :disabled="signing"
          @click="askRevoke"
        ><fa-icon :icon="['fal', 'ban']" />Revogar autorização</button>
      </div>
    </template>

    <template v-else>
      <div class="ds-auth-error" v-if="authError">
        <fa-icon :icon="['fal', 'exclamation-triangle']" />
        <div>
          <div class="ds-auth-error-title">CPF ou código inválido</div>
          <div class="ds-auth-error-description">
            Gere um novo código no app Bird ID e tente de novo.
          </div>
        </div>
      </div>

      <div class="form-group" :class="{'has-error': $v.form.username.$error}">
        <label for="digital-signature-username" class="form-label">Usuário (CPF)</label>
        <input
          class="form-input"
          id="digital-signature-username"
          v-model="form.username"
          @change="$v.form.username.$touch()"
          autocomplete="username"
          inputmode="numeric"
          placeholder="CPF cadastrado no Bird ID"
          :disabled="signing"
        />
        <template v-if="$v.form.username.$error">
          <div
            class="form-input-hint"
            v-if="!$v.form.username.required"
          >Campo obrigatório</div>
        </template>
        <div class="ds-field-hint" v-else>O mesmo CPF do seu cadastro no Bird ID</div>
      </div>

      <div class="form-group" :class="{'has-error': $v.form.password.$error}">
        <label for="digital-signature-password" class="form-label">Código do app (OTP)</label>
        <input
          class="form-input ds-code-input"
          id="digital-signature-password"
          v-model="form.password"
          @change="$v.form.password.$touch()"
          autocomplete="one-time-code"
          inputmode="numeric"
          placeholder="000000"
          :disabled="signing"
        />
        <template v-if="$v.form.password.$error">
          <div
            class="form-input-hint"
            v-if="!$v.form.password.required"
          >Campo obrigatório</div>
        </template>
        <div class="ds-field-hint" v-else>
          Código de 6 dígitos do app Bird ID. Use sempre um código novo.
        </div>
      </div>

      <div class="form-group mb-0">
        <label id="digital-signature-expiry" class="form-label">Validade da autorização</label>
        <div class="ds-lifetimes" role="radiogroup" aria-labelledby="digital-signature-expiry">
          <button
            v-for="item in lifetimeItems"
            :key="item.value"
            type="button"
            role="radio"
            class="ds-lifetime"
            :class="{ 'is-active': form.lifetime === item.value }"
            :aria-checked="form.lifetime === item.value ? 'true' : 'false'"
            :disabled="signing"
            @click="form.lifetime = item.value"
          >{{ item.text }}</button>
        </div>
        <div class="ds-field-hint">
          <fa-icon :icon="['fal', 'clock']" />
          Você não precisará informar o código de novo até {{ lifetimeUntil }}.
        </div>
      </div>
    </template>

    <template v-slot:footer>
      <button
        type="button"
        class="btn btn-icon btn-icon-left btn-primary ml-2"
        :class="{ loading: signing }"
        @click="authenticateAndSign"
        :disabled="signing || loading || revoking.confirm"
      ><fa-icon :icon="['fal', 'signature']" />
        {{ token.isValid || loading ? 'Assinar' : 'Autorizar e assinar' }}</button>
      <button
        type="button"
        class="btn ml-2"
        :disabled="busy"
        @click="close"
      >Cancelar</button>
    </template>
  </dx-modal>
</template>

<script>
import moment from 'moment/moment';
import { required } from 'vuelidate/lib/validators';
import store from '@/store';

const lifetimeItems = [
  {
    value: 60 * 60,
    text: '1 hora',
  },
  {
    value: 60 * 60 * 8,
    text: '8 horas',
  },
  {
    value: 60 * 60 * 24,
    text: '1 dia',
  },
  {
    value: 60 * 60 * 24 * 3,
    text: '3 dias',
  },
  {
    value: 60 * 60 * 24 * 7,
    text: '7 dias',
  },
];

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    sign: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      loading: true,
      signing: false,
      authError: false,
      revoking: {
        confirm: false,
        loading: false,
      },
      form: this.blankForm(),
      token: {
        isValid: false,
        expiresAt: null,
      },
      lifetimeItems,
    };
  },
  validations: {
    form: {
      username: { required },
      password: { required },
    },
  },
  mounted() {
    this.init();
  },
  watch: {
    'form.username': function clearUsernameError() {
      this.authError = false;
    },
    'form.password': function clearPasswordError() {
      this.authError = false;
    },
  },
  computed: {
    user() {
      return store.state.auth.user;
    },
    busy() {
      return this.signing || this.revoking.loading;
    },
    expiresIn() {
      if (!this.token.expiresAt) return '';
      return moment().to(this.token.expiresAt);
    },
    expiresAtLabel() {
      if (!this.token.expiresAt) return '';
      return moment(this.token.expiresAt).format('DD/MM/YYYY [às] HH:mm');
    },
    lifetimeUntil() {
      return moment().add(this.form.lifetime, 'seconds').format('DD/MM [às] HH:mm');
    },
  },
  methods: {
    async init() {
      this.form.username = this.user.identity.value;
      await this.check();
    },
    setTokenData(data) {
      this.token.isValid = data.isValid;
      this.token.expiresAt = data.expiresAt;
    },
    async check() {
      this.loading = true;
      try {
        const { data } = await this.$http.get('/digital-signature/access-tokens/status');
        this.setTokenData(data);
      } catch (e) {
        this.$toast.error(e);
      }
      this.loading = false;
      this.$emit('ready');
    },
    async authenticateAndSign() {
      if (this.busy) {
        return;
      }

      if (!this.token.isValid) {
        this.$v.form.$touch();
        if (this.$v.form.$error) {
          return;
        }
      }

      this.signing = true;
      try {
        if (!this.token.isValid) {
          const { data } = await this.$http.post('/digital-signature/access-tokens', this.form);
          this.setTokenData(data);
        }
        await this.sign();
      } catch (e) {
        this.authError = this.isAuthenticationError(e);
        this.$toast.error(e);
      }
      this.signing = false;
    },
    isAuthenticationError(e) {
      const data = e && e.response ? e.response.data : null;
      const text = [
        data && data.reason,
        data && data.message,
        data && data.error,
        e && e.message,
      ].filter(v => typeof v === 'string').join(' ');
      return /failedToAuthenticateUser|invalid credentials|falha ao autenticar/i.test(text);
    },
    askRevoke() {
      this.revoking.confirm = true;
    },
    cancelRevoke() {
      if (this.revoking.loading) {
        return;
      }
      this.revoking.confirm = false;
    },
    async revoke() {
      if (this.revoking.loading) {
        return;
      }

      this.revoking.loading = true;
      try {
        const { data } = await this.$http.delete('/digital-signature/access-tokens');
        this.setTokenData(data && typeof data === 'object'
          ? data
          : { isValid: false, expiresAt: null });
        this.form.password = '';
        this.$v.form.$reset();
        this.revoking.confirm = false;
        this.$toast.show('Autorização revogada');
      } catch (e) {
        this.$toast.error(e);
      }
      this.revoking.loading = false;
    },
    close() {
      if (this.busy) {
        return;
      }
      this.form = this.blankForm();
      this.$emit('close');
    },
    blankForm() {
      return {
        username: '',
        password: '',
        lifetime: lifetimeItems[1].value,
      };
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";

#digital-signature-modal {
  .modal-container {
    max-width: 24rem;
  }
  .btn.ds-revoke-button {
    background-color: $light-color;
    border-color: $error-color;
    color: $error-color;
    &:hover, &:focus {
      background-color: rgba($error-color, .08);
      border-color: darken($error-color, 8%);
      box-shadow: none;
      color: darken($error-color, 8%);
    }
  }
  .modal-header {
    padding: $layout-spacing-lg $layout-spacing-lg $layout-spacing;
  }
  .modal-body {
    padding: $layout-spacing $layout-spacing-lg;
  }
  .modal-footer {
    padding: $layout-spacing $layout-spacing-lg $layout-spacing-lg;
  }
  .ds-header {
    align-items: center;
    display: flex;
    gap: .6rem;
  }
  .ds-header-icon {
    align-items: center;
    background-color: lighten($primary-color, 34%);
    border-radius: 50%;
    color: $primary-color;
    display: flex;
    flex: 0 0 2rem;
    font-size: $font-size-lg;
    height: 2rem;
    justify-content: center;
  }
  .ds-header-text {
    min-width: 0;
  }
  .ds-header-title {
    color: $gray-color-900;
    font-size: $font-size-lg;
    font-weight: 700;
    line-height: 1.25;
  }
  .ds-header-subtitle {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    line-height: 1.4;
    margin-top: .15rem;
  }
  .ds-providers {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: .3rem;
    margin-bottom: .8rem;
  }
  .ds-providers-label {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    margin-right: .2rem;
  }
  .ds-provider {
    align-items: center;
    background-color: $light-color;
    border: $border-width solid $border-color;
    border-radius: 1rem;
    color: $gray-color-dark;
    cursor: default;
    display: inline-flex;
    font-size: $font-size-sm;
    font-weight: 600;
    gap: .25rem;
    line-height: 1;
    padding: .25rem .6rem;
    &.is-active {
      border-color: #BFE3CD;
      color: #1F7A4E;
    }
    &:disabled {
      opacity: 1;
    }
  }
  .ds-provider-soon {
    color: $gray-color-dark;
    font-size: $font-size-xs;
    font-weight: 400;
    margin-left: .25rem;
  }
  .ds-skeleton {
    min-height: 13rem;
  }
  .ds-skeleton-group {
    margin-bottom: .8rem;
  }
  .ds-skeleton-line {
    animation: ds-skeleton-pulse 1.2s ease-in-out infinite;
    background-color: $gray-color-ultra-light;
    border-radius: $border-radius;
    &.is-label {
      height: .6rem;
      margin-bottom: .3rem;
      width: 30%;
    }
    &.is-input {
      height: 1.8rem;
    }
  }
  .ds-status-icon {
    align-items: center;
    background-color: $light-color;
    border-radius: 50%;
    box-shadow: 0 .05rem .2rem rgba($dark-color, .12);
    display: flex;
    flex: 0 0 1.8rem;
    font-size: $font-size-lg;
    height: 1.8rem;
    justify-content: center;
    &.is-success {
      color: #2FA36B;
    }
    &.is-error {
      color: $error-color;
    }
  }
  .ds-status-text {
    flex: 1 1 10rem;
    min-width: 0;
  }
  .ds-status-title {
    color: $gray-color-900;
    font-weight: 700;
  }
  .ds-status-description {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    line-height: 1.45;
    margin-top: .05rem;
  }
  .ds-status-hint {
    color: $gray-color-dark;
    font-size: $font-size-xs;
    margin-top: .2rem;
  }
  .ds-active {
    align-items: center;
    background-color: #F2FAF5;
    border: $border-width solid #BFE3CD;
    border-left: .2rem solid #2FA36B;
    border-radius: $border-radius;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing .6rem;
    padding: .7rem .8rem;
    .ds-status-title {
      color: #1F7A4E;
    }
    .btn {
      margin-left: auto;
    }
  }
  .ds-revoke-confirm {
    background-color: #FDF2F2;
    border: $border-width solid #F1C4C4;
    border-left: .2rem solid $error-color;
    border-radius: $border-radius;
    padding: .7rem .8rem;
  }
  .ds-revoke-confirm-head {
    align-items: flex-start;
    display: flex;
    gap: .6rem;
  }
  .ds-revoke-confirm-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: .6rem;
  }
  .ds-auth-error {
    align-items: flex-start;
    background-color: #FDF2F2;
    border: $border-width solid #F1C4C4;
    border-radius: $border-radius;
    color: $error-color;
    display: flex;
    gap: .5rem;
    margin-bottom: .8rem;
    padding: .5rem .7rem;
    svg {
      flex-shrink: 0;
      margin-top: .15rem;
    }
  }
  .ds-auth-error-title {
    color: $gray-color-900;
    font-weight: 600;
  }
  .ds-auth-error-description {
    color: $gray-color-dark;
    font-size: $font-size-sm;
  }
  .ds-field-hint {
    color: $gray-color-dark;
    font-size: $font-size-xs;
    line-height: 1.4;
    margin-top: .2rem;
    svg {
      margin-right: .15rem;
    }
  }
  .ds-code-input {
    font-size: $font-size-lg;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    letter-spacing: .2rem;
    &::placeholder {
      font-weight: 400;
      letter-spacing: .2rem;
    }
  }
  .ds-lifetimes {
    display: flex;
    flex-wrap: wrap;
    gap: .3rem;
  }
  .ds-lifetime {
    background-color: $light-color;
    border: $border-width solid $border-color;
    border-radius: 1rem;
    color: $gray-color-900;
    cursor: pointer;
    flex: 1 1 auto;
    font-size: $font-size-sm;
    line-height: 1;
    padding: .35rem .5rem;
    transition: background-color .15s, border-color .15s, color .15s;
    white-space: nowrap;
    &:hover:not(:disabled) {
      background-color: $gray-color-ultra-light;
    }
    &.is-active {
      background-color: lighten($primary-color, 34%);
      border-color: $primary-color;
      color: $primary-color;
      font-weight: 600;
    }
    &:disabled {
      cursor: default;
      opacity: .6;
    }
  }
}

.digital-signature-preloader {
  align-items: center;
  background: rgba($dark-color, .75);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 400;
  .preloader-box {
    align-items: center;
    background-color: $light-color;
    border-radius: $border-radius * 2;
    box-shadow: 0 .4rem 1.2rem rgba($dark-color, .25);
    color: $gray-color-900;
    display: flex;
    gap: .6rem;
    padding: .8rem 1.1rem;
  }
  .preloader-spinner {
    height: 1rem;
    position: relative;
    width: 1rem;
  }
  .preloader-text {
    color: $gray-color-dark;
    font-size: $font-size-sm;
  }
}

@keyframes ds-skeleton-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: .45;
  }
}
</style>
